// src/hooks/usePresence.js
import { useEffect, useRef } from "react";
import { ref, set, onDisconnect, serverTimestamp } from "firebase/database";
import { auth, rtdb } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

// FIX: exported so logout handlers can call it before signOut()
export async function markUserOffline(uid) {
  if (!uid) return;
  try {
    await set(ref(rtdb, `presence/${uid}`), {
      online: false,
      lastSeen: serverTimestamp(),
    });
  } catch {}
}

export default function usePresence() {
  const currentRef = useRef(null);
  const heartbeatInterval = useRef(null);
  const visibilityHandler = useRef(null);

  useEffect(() => {
    const clearPresence = async () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
        heartbeatInterval.current = null;
      }
      if (visibilityHandler.current) {
        document.removeEventListener(
          "visibilitychange",
          visibilityHandler.current
        );
        visibilityHandler.current = null;
      }
      if (currentRef.current) {
        try {
          await set(currentRef.current, {
            online: false,
            lastSeen: serverTimestamp(),
          });
        } catch {}
        currentRef.current = null;
      }
    };

    const registerOnDisconnect = (userRef, uid, displayName, email) => {
      onDisconnect(userRef).set({
        uid,
        online: false,
        lastSeen: serverTimestamp(),
        displayName,
        email,
      });
    };

    const startPresence = async (user) => {
      if (!user) return;

      const uid = user.uid;
      const displayName = user.displayName || "Anonymous";
      const email = user.email || null;
      const userRef = ref(rtdb, `presence/${uid}`);
      currentRef.current = userRef;

      await set(userRef, {
        uid,
        online: true,
        lastSeen: serverTimestamp(),
        displayName,
        email,
      });

      registerOnDisconnect(userRef, uid, displayName, email);

      heartbeatInterval.current = setInterval(() => {
        set(userRef, {
          uid,
          online: true,
          lastSeen: serverTimestamp(),
          displayName,
          email,
        });
        registerOnDisconnect(userRef, uid, displayName, email);
      }, 30000);

      const handleVisibility = () => {
        set(userRef, {
          uid,
          online: document.visibilityState !== "hidden",
          lastSeen: serverTimestamp(),
          displayName,
          email,
        });
      };
      visibilityHandler.current = handleVisibility;
      document.addEventListener("visibilitychange", handleVisibility);
    };

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      await clearPresence();
      if (user) await startPresence(user);
    });

    return () => {
      clearPresence();
      unsubAuth();
    };
  }, []);
}